import { useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { Toaster } from 'sonner';
import { CartProvider } from '@/context/CartContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';
import ChatWidget from '@/components/ChatWidget';
import { SeoManager } from '@/components/SeoManager';
import { redirectFor, redirectSearch } from '@/lib/seoPolicy';
import Home from '@/pages/Home';
import AuthCallback from '@/pages/AuthCallback';
import NotFound from '@/pages/NotFound';

// Load route-specific tools and administration only when they are visited.
const TuningFiles = lazy(() => import('@/pages/TuningFiles'));
const Tools = lazy(() => import('@/pages/Tools'));
const ShopIndex = lazy(() => import('@/pages/Shop'));
const ShopAll = lazy(() => import('@/pages/Shop').then(m => ({ default: m.ShopAll })));
const ShopBrand = lazy(() => import('@/pages/Shop').then(m => ({ default: m.ShopBrand })));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Dealer = lazy(() => import('@/pages/Dealer'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Legal = lazy(() => import('@/pages/Legal'));
const Autotuner = lazy(() => import('@/pages/Autotuner'));
const MagicFlex = lazy(() => import('@/pages/MagicFlex'));
const FlexVehicles = lazy(() => import('@/pages/FlexVehicles'));
const Kess3 = lazy(() => import('@/pages/Kess3'));
const Kess3Builder = lazy(() => import('@/pages/Kess3Builder'));
const FlexBuilder = lazy(() => import('@/pages/FlexBuilder'));
const TuningDatabase = lazy(() => import('@/pages/TuningDatabase'));
const VehicleTuning = lazy(() => import('@/pages/VehicleTuning'));
const MakeTuning = lazy(() => import('@/pages/MakeTuning'));
const TuningCategoryIndex = lazy(() => import('@/pages/TuningCategoryIndex'));
const TuningCategoryMake = lazy(() => import('@/pages/TuningCategoryMake'));
const TuningCategoryModel = lazy(() => import('@/pages/TuningCategoryModel'));
const TuningCategoryVehicle = lazy(() => import('@/pages/TuningCategoryVehicle'));
const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const AdminProducts = lazy(() => import('@/pages/admin/AdminProducts'));
const AdminEdit = lazy(() => import('@/pages/admin/AdminEdit'));
const AdminFlex = lazy(() => import('@/pages/admin/AdminFlex'));
const AdminOrders = lazy(() => import('@/pages/admin/AdminOrders'));
const AdminKess3 = lazy(() => import('@/pages/admin/AdminKess3'));
const AdminTuningData = lazy(() => import('@/pages/admin/AdminTuningData'));
const SignIn = lazy(() => import('@/pages/SignIn'));
const Account = lazy(() => import('@/pages/Account'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('@/pages/PaymentCancel'));

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Client fallback only; real HTTP 301s require the same map at the live edge.
function LegacyRedirectGate({ children }) {
  const { pathname, search, hash } = useLocation();
  const dest = redirectFor(pathname, search);
  if (dest) {
    return <Navigate to={dest + redirectSearch(pathname, search, dest) + hash} replace />;
  }
  return children;
}

function OAuthReturnGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return children;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = t => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <ScrollManager />
          <SeoManager />
          <OAuthReturnGate>
            <LegacyRedirectGate>
              <Header />
              <CartDrawer />
              <Suspense fallback={<main className="min-h-screen" data-testid="page-loading" role="status"><span className="sr-only">Loading page</span></main>}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/tuning-files" element={<TuningFiles />} />
                  <Route path="/tuning-tools" element={<Tools />} />
                  <Route path="/tuning-tools/:category" element={<Tools />} />
                  <Route path="/shop" element={<ShopIndex />} />
                  <Route path="/shop/all" element={<ShopAll />} />
                  <Route path="/shop/alientech/updates" element={<ShopBrand brandSlug="alientech" collection="updates" />} />
                  <Route path="/shop/:brand" element={<ShopBrand />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/autotuner" element={<Autotuner />} />
                  <Route path="/magicmotorsport-flex" element={<MagicFlex />} />
                  <Route path="/flex-vehicle-list" element={<FlexVehicles />} />
                  <Route path="/alientech-kess3" element={<Kess3 />} />
                  <Route path="/tuning-database" element={<TuningDatabase />} />
                  <Route path="/tuning-database/trucks" element={<TuningCategoryIndex category="trucks" />} />
                  <Route path="/tuning-database/trucks/:make" element={<TuningCategoryMake category="trucks" />} />
                  <Route path="/tuning-database/trucks/:make/:model" element={<TuningCategoryModel category="trucks" />} />
                  <Route path="/tuning-database/trucks/:make/:model/:generation/:engine" element={<TuningCategoryVehicle category="trucks" />} />
                  <Route path="/tuning-database/agriculture" element={<TuningCategoryIndex category="agriculture" />} />
                  <Route path="/tuning-database/agriculture/:make" element={<TuningCategoryMake category="agriculture" />} />
                  <Route path="/tuning-database/agriculture/:make/:model" element={<TuningCategoryModel category="agriculture" />} />
                  <Route path="/tuning-database/agriculture/:make/:model/:generation/:engine" element={<TuningCategoryVehicle category="agriculture" />} />
                  <Route path="/tuning-database/:make/:model/:engine" element={<TuningDatabase />} />
                  <Route path="/tuning/:make" element={<MakeTuning />} />
                  <Route path="/tuning/:make/:model/:generation/:engine" element={<VehicleTuning />} />
                  <Route path="/become-a-dealer" element={<Dealer />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/legal/:page" element={<Legal />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminProducts />} />
                  <Route path="/admin/new" element={<AdminEdit />} />
                  <Route path="/admin/edit/:id" element={<AdminEdit />} />
                  <Route path="/admin/flex" element={<AdminFlex />} />
                  <Route path="/admin/orders" element={<AdminOrders />} />
                  <Route path="/build-your-flex/:type" element={<FlexBuilder />} />
                  <Route path="/build-your-kess3/:type" element={<Kess3Builder />} />
                  <Route path="/admin/kess3" element={<AdminKess3 />} />
                  <Route path="/admin/tuning-data" element={<AdminTuningData />} />
                  <Route path="/sign-in" element={<SignIn />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <Footer />
              <ChatWidget />
            </LegacyRedirectGate>
          </OAuthReturnGate>
        </CartProvider>
      </BrowserRouter>
      <Toaster theme="light" position="bottom-left" toastOptions={{ style: { background: '#FFFFFF', border: '1px solid #E3E8EF', color: '#1D2939' } }} />
    </div>
  );
}
export default App;